/**
 * Utilidades y funciones helper para la aplicación
 */

/**
 * Formatea una fecha a formato relativo (hace X días/horas)
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Nunca';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Hace un momento';
  if (diffMins < 60) return `Hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
  if (diffHours < 24) return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
  if (diffDays < 7) return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
  
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) return `Hace ${diffWeeks} ${diffWeeks === 1 ? 'semana' : 'semanas'}`;
  
  const diffMonths = Math.floor(diffDays / 30);
  return `Hace ${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`;
};

/**
 * Formatea una fecha a formato legible
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Calcula el progreso porcentual
 */
export const calculateProgress = (current, initial, target) => {
  if (target === initial) return 0;
  const progress = ((current - initial) / (target - initial)) * 100;
  return Math.max(0, Math.min(100, Math.round(progress * 100) / 100));
};

/**
 * Parsea un período (Q1 2026) a objeto
 */
export const parsePeriod = (periodString) => {
  const match = periodString.match(/Q(\d)\s+(\d{4})/);
  if (!match) return null;
  
  return {
    period: periodString,
    quarter: parseInt(match[1]),
    year: parseInt(match[2]),
  };
};

/**
 * Calcula el número de semana actual del trimestre
 */
export const getCurrentWeek = (period) => {
  const parsed = parsePeriod(period);
  if (!parsed) return 1;
  
  const quarterStart = new Date(parsed.year, (parsed.quarter - 1) * 3, 1);
  const now = new Date();
  const diffMs = now - quarterStart;
  const diffDays = Math.floor(diffMs / 86400000);
  const week = Math.floor(diffDays / 7) + 1;
  
  return Math.max(1, Math.min(17, week));
};

/**
 * Calcula el progreso esperado basado en la semana actual
 */
export const getExpectedProgress = (currentWeek) => {
  return (currentWeek / 17) * 100;
};

/**
 * Determina el estado de un OKR basado en progreso
 */
export const getOKRStatus = (actualProgress, expectedProgress) => {
  if (actualProgress >= expectedProgress) {
    return { status: 'on-track', label: 'En camino', color: 'green', emoji: '🟢' };
  } else if (actualProgress >= expectedProgress * 0.5) {
    return { status: 'at-risk', label: 'En riesgo', color: 'yellow', emoji: '🟡' };
  } else {
    return { status: 'off-track', label: 'Fuera de camino', color: 'red', emoji: '🔴' };
  }
};

/**
 * Genera un color para un badge de estado
 */
export const getStatusColor = (status) => {
  const colors = {
    active: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    archived: 'bg-gray-100 text-gray-800',
    planning: 'bg-yellow-100 text-yellow-800',
    'at-risk': 'bg-orange-100 text-orange-800',
  };
  return colors[status] || colors.active;
};

/**
 * Genera un password aleatorio
 */
export const generateRandomPassword = (length = 12) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

/**
 * Valida un email
 */
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Trunca un texto a una longitud máxima
 */
export const truncate = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
